import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, TrendingUp } from "lucide-react";

const InteractiveCalculator = () => {
  const [originalPrice, setOriginalPrice] = useState(100000);
  const [originalRate, setOriginalRate] = useState(8);
  const [discountPrice, setDiscountPrice] = useState(85000);
  const [monthlyPayment, setMonthlyPayment] = useState(1200);

  const calculateYield = () => {
    if (discountPrice === 0) return 0;
    const annualPayment = monthlyPayment * 12;
    return ((annualPayment / discountPrice) * 100).toFixed(1);
  };

  const calculateDiscount = () => {
    if (originalPrice === 0) return 0;
    return (((originalPrice - discountPrice) / originalPrice) * 100).toFixed(1);
  };

  const resetExample = () => {
    setOriginalPrice(100000);
    setOriginalRate(8);
    setDiscountPrice(85000);
    setMonthlyPayment(1200);
  };

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Interactive Yield Calculator
          </h2>
          <p className="text-xl text-gray-600">
            See how buying notes at a discount increases your investment yield
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Investment Calculator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="originalPrice">Original Note Value</Label>
                  <Input
                    id="originalPrice"
                    type="number"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(Number(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="originalRate">Original Rate (%)</Label>
                  <Input
                    id="originalRate"
                    type="number"
                    value={originalRate}
                    onChange={(e) => setOriginalRate(Number(e.target.value))}
                    className="mt-1"
                    step="0.1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="discountPrice">Your Purchase Price</Label>
                  <Input
                    id="discountPrice"
                    type="number"
                    value={discountPrice}
                    onChange={(e) => setDiscountPrice(Number(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="monthlyPayment">Monthly Payment</Label>
                  <Input
                    id="monthlyPayment"
                    type="number"
                    value={monthlyPayment}
                    onChange={(e) => setMonthlyPayment(Number(e.target.value))}
                    className="mt-1"
                  />
                </div>
              </div>

              <Button onClick={resetExample} variant="outline" className="w-full">
                Reset to Example Values
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Your Investment Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {calculateYield()}%
                    </div>
                    <div className="text-gray-700 font-medium">Your Effective Yield</div>
                    <div className="text-sm text-gray-600 mt-1">
                      vs {originalRate}% original rate
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {calculateDiscount()}%
                    </div>
                    <div className="text-sm text-gray-600">Discount</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">
                      ${(monthlyPayment * 12).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Annual Income</div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">The Math</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Annual Payment: ${(monthlyPayment * 12).toLocaleString()}</div>
                    <div>Your Investment: ${discountPrice.toLocaleString()}</div>
                    <div>Effective Yield: {calculateYield()}%</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default InteractiveCalculator;